import { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { Button, Spinner, SpinnerSize, Switch } from "@blueprintjs/core";
import {
  faCloudUpload,
  faDownload,
  faExclamationTriangle,
  faQuestionCircle,
  faSave,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "@mantine/core";

import { AppToaster } from "@components";
import { OrgContext } from "@providers";
import { Feature, withToaster } from "@utils";

import { WorkbenchContext } from "@pages/editor/providers";
import { downloadFile } from "@pages/editor/utils";
import {
  publishRule,
  publishRuleset,
  updateVisibility,
} from "@pages/registry/api";

import { ConfirmVisibilitySwitchDialog } from "./ConfirmVisibilitySwitchDialog";
import { PrCreatedDialog } from "./PrCreatedDialog";
import { PublicRuleChecklist } from "./PublicRuleChecklist";
import { RegistryRuleShareDialog } from "./RegistryRuleShareDialog";
import { RuleSharePreview } from "./RuleSharePreview";
import {
  CenterAlign,
  CleanDialog,
  DialogBody,
  VisibilityWarning,
} from "./RuleShareUtils";
import { VisibilityToggle } from "./VisibilityToggle";

const RegistrySection = styled.div`
  display: flex;
  gap: 8px;
`;

const UnlistedRuleURL: React.FC<{ input: string }> = ({ input }) => {
  return <RuleSharePreview input={"https://semgrep.dev/s/" + input} />;
};

export type ChangeVisibilityAction =
  | "unlisted"
  | "org_private"
  | "public" // repo, goes to publish flow
  | undefined;

/**
 * Component that encapsulates all the rule share dialog states
 * This is a complicated component with many uses. You can
 * - change a rule's visibility
 * - publish a rule to the registry (happens when you set visibility to public)
 * - get a permalink (creates a snippet)
 */
export const RuleShareDialog = () => {
  const { workbench } = useContext(WorkbenchContext);
  const visibility = workbench.bundle?.visibility;
  const address = workbench.address;

  const [action, setAction] = useState<ChangeVisibilityAction>(
    workbench.ui.ruleShareDialogIsPublic ? "public" : undefined
  );
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [prURL, setPrURL] = useState<string | undefined>(undefined);
  const [ruleOrSnippetId, setRuleOrSnippetId] = useState(
    address?.fullName ?? undefined
  );
  const [isSnippetLink, setIsSnippetLink] = useState<boolean>(
    workbench.user === undefined
  );
  const [isLoadingSnippetLink, setIsLoadingSnippetLink] = useState(false);

  // Required to block out non-team-tier permissions:
  const { org } = useContext(OrgContext);
  const hasTeamsTier = Feature.hasTeamsTier({ org });

  // content for the tooltip that appears if the publish to registry button is disabled
  let publishToRegistryTooltipContent = undefined;
  if (workbench.user === undefined) {
    publishToRegistryTooltipContent =
      "Log in to contribute to the Semgrep Registry";
  } else if (workbench.hasUnsavedChanges) {
    publishToRegistryTooltipContent =
      "Save your changes first before publishing";
  } else if (workbench.isDefaultNewRule) {
    publishToRegistryTooltipContent = "Write a rule to be able to publish it";
  }

  useEffect(() => {
    if (isSnippetLink) {
      // generate snippet link
      setIsLoadingSnippetLink(true);
      workbench.bundle
        ?.saveSnippet()
        .then((resp) => {
          if (typeof resp === "string") {
            setRuleOrSnippetId(resp as string);
          } else {
            // error occurred
            setRuleOrSnippetId(undefined);
          }
        })
        .catch((err) => {
          setRuleOrSnippetId(undefined);
          AppToaster.show({
            message: `Failed to save rule: ${err}`,
            intent: "danger",
          });
        })
        .finally(() => setIsLoadingSnippetLink(false));
    } else {
      if (!address) {
        // if this is a new rule that hasn't been saved before,
        // the address will be undefined
        setRuleOrSnippetId(undefined);
      } else {
        setRuleOrSnippetId(address.fullName);
      }
    }
    // disablign exhaustive deps because we don't want this to run every time bundle changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSnippetLink,
    setIsLoadingSnippetLink,
    setRuleOrSnippetId,
    workbench.bundle?.saveSnippet,
    address?.fullName,
  ]);

  /**
   * Creates PR using publish api
   */
  const onCreatePRClick = () => {
    // todo check for unsaved changes
    // cannot create a PR if there are unsaved changes, or if the address or rule
    // don't exist.
    if (workbench.hasUnsavedChanges || !address || !workbench.bundle?.rule)
      return;
    setIsPublishing(true);
    const publish = address.fullName.includes(":")
      ? publishRuleset
      : publishRule;
    withToaster(
      () => publish(address.fullName, workbench.bundle!.rule!.id),
      (err) => {
        setIsPublishing(false);
        return `Error publishing: ${err.body?.error}`;
      }
    )().then(({ url }) => {
      setIsPublishing(false);
      setPrURL(url);
    });
  };

  const onSaveChangeClick = (newVisibility: "unlisted" | "org_private") => {
    // should not get here if there is no valid address to save changes to
    if (!address) return;
    setIsPublishing(true);
    withToaster(
      () => updateVisibility(address.fullName, newVisibility, org!.id),
      (err) => {
        setIsPublishing(false);
        return `Error updating: ${err.body?.error}`;
      }
    )().then(() => {
      if (!workbench.addressString) return;

      Promise.all([
        workbench.fetchBundle(workbench.addressString),
        workbench.fileRoot.reloadSnippetsAndRulesets(),
      ]).then(() => {
        setIsPublishing(false);
      });
    });
  };

  if (prURL) {
    return (
      <PrCreatedDialog
        onClose={workbench.ui.closeRuleShareDialog}
        prURL={prURL}
      />
    );
  } else if (isPublishing) {
    // publishing loading state
    return (
      <CleanDialog
        isOpen={true}
        onClose={workbench.ui.closeRuleShareDialog}
        title={`Processing`}
      >
        <DialogBody>
          <CenterAlign
            style={{
              padding: "32px",
            }}
          >
            <Spinner intent="primary" size={SpinnerSize.LARGE} />
          </CenterAlign>
        </DialogBody>
      </CleanDialog>
    );
  } else if (action === "org_private" || action === "unlisted") {
    return (
      <ConfirmVisibilitySwitchDialog
        isSwitchToPrivate={action === "org_private"}
        onCancel={() => setAction(undefined)}
        onConfirm={() => {
          onSaveChangeClick(action);
          setAction(undefined);
        }}
      />
    );
  } else if (visibility === "public") {
    return <RegistryRuleShareDialog />;
  } else if (action === "public") {
    return (
      <PublicRuleChecklist
        onCreatePRClick={onCreatePRClick}
        workbench={workbench}
        onClose={workbench.ui.closeRuleShareDialog}
      />
    );
  }

  const SNIPPET_PREVIEW = isLoadingSnippetLink ? (
    <RuleSharePreview input={"Loading version link..."} />
  ) : ruleOrSnippetId ? (
    <UnlistedRuleURL input={ruleOrSnippetId} />
  ) : (
    // Error state
    <RuleSharePreview
      input={"Invalid rule error, try running your rule!"}
      leftElement={
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          style={{ margin: "0 4px" }}
        />
      }
    />
  );

  // will get here if action === undefined (user is not trying to change the visibility)
  // and the rule is not a public registry rule
  return (
    <CleanDialog
      isOpen={true}
      onClose={workbench.ui.closeRuleShareDialog}
      title="Share your rule"
    >
      <DialogBody>
        <div style={{ marginBottom: "16px" }}>
          {isSnippetLink ? (
            SNIPPET_PREVIEW
          ) : ruleOrSnippetId ? (
            <UnlistedRuleURL input={ruleOrSnippetId} />
          ) : address === undefined ? (
            // no saved version of the rule to show the address of
            <RuleSharePreview
              input={"Save your rule first!"}
              leftElement={
                <FontAwesomeIcon icon={faSave} style={{ margin: "0 4px" }} />
              }
            />
          ) : (
            <RuleSharePreview
              input={"Invalid rule error, try running your rule!"}
              leftElement={
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  style={{ margin: "0 4px" }}
                />
              }
            />
          )}
          <VisibilityWarning>
            {visibility === "unlisted" || isSnippetLink
              ? "Visible to your organization and to anyone with the link"
              : `Visible only to logged in users of ${org?.name}.`}
          </VisibilityWarning>
        </div>
        <VisibilityToggle
          hasTeamsTier={hasTeamsTier}
          visibility={visibility!}
          setAction={setAction}
        />
        <Switch
          inline
          checked={isSnippetLink}
          onChange={() => setIsSnippetLink(!isSnippetLink)}
          disabled={workbench.user === undefined}
          style={{ margin: "0 0 0 16px" }}
        />
        <span>
          Permalink{" "}
          <Tooltip
            withArrow
            withinPortal
            multiline
            width={300}
            label={
              <>
                <div>
                  Permalinks go to this particular version of the rule. Changes
                  to the rule won't be reflected at this link.
                </div>
                {workbench.user === undefined && (
                  <div>
                    To create share links that include updates to rules, log in.
                  </div>
                )}
              </>
            }
          >
            <FontAwesomeIcon icon={faQuestionCircle} size={"xs"} />
          </Tooltip>
        </span>
        {isSnippetLink && (
          <VisibilityWarning style={{ color: "var(--global-red)" }}>
            Permalinks are viewable by anyone with a link.
          </VisibilityWarning>
        )}
      </DialogBody>
      <DialogBody
        style={{
          backgroundColor: "var(--light-gray1)",
        }}
      >
        <RegistrySection>
          <div style={{ marginRight: "auto" }}>
            Share your rule with the Semgrep community{" "}
          </div>
          <Tooltip
            label={publishToRegistryTooltipContent}
            position="left"
            withArrow
            withinPortal
          >
            <div>
              <Button
                onClick={() => {
                  setAction("public");
                }}
                intent="primary"
                outlined
                disabled={
                  workbench.isDefaultNewRule || workbench.hasUnsavedChanges
                }
                icon={<FontAwesomeIcon icon={faCloudUpload} />}
              >
                Publish to Registry
              </Button>
            </div>
          </Tooltip>
          <Button
            intent="primary"
            outlined
            onClick={() =>
              downloadFile(
                workbench.bundle?.rule?.id + ".yaml",
                workbench.bundle?.ruleText ?? ""
              )
            }
          >
            <FontAwesomeIcon icon={faDownload} />
          </Button>
        </RegistrySection>
      </DialogBody>
    </CleanDialog>
  );
};
